import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Plus, Search, Pencil, Trash2, ChefHat, X, ImagePlus, Leaf, AlertTriangle,
  ChevronRight, Sparkles, FileText, UtensilsCrossed,
} from "lucide-react";
import api, { formatApiError } from "../api";
import {
  Button, Input, Label, Select, Card, Badge, Spinner, EmptyState,
} from "../components/ui/Primitives";
import {
  formatEUR, formatNumber, RECIPE_CATEGORY_LABELS, UNIT_LABELS, ALLERGEN_ABBR, parseATDecimal,
} from "../utils/format";
import { lineCost, UNIT_COMPATIBLE } from "../utils/units";
import RecipePdfImportModal from "../components/RecipePdfImportModal";
import UnifiedImportButton from "../components/UnifiedImportButton";
import IngredientMatchSuggestion from "../components/IngredientMatchSuggestion";
import PlatingModal from "../components/PlatingModal";
import useAutoSaveDraft from "../hooks/useAutoSaveDraft";
import DraftRestoreBanner from "../components/DraftRestoreBanner";
import { useAuth } from "../contexts/AuthContext";

const STATUS_TONES = { active: "ok", inactive: "default", seasonal: "warn" };

function TrafficDot({ light }) {
  const colors = { green: "bg-ok", yellow: "bg-warn", red: "bg-err", neutral: "bg-ink-muted" };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[light] || colors.neutral}`} />;
}

function AllergenChips({ items = [], compact = true }) {
  const { t } = useTranslation();
  if (!items.length) return <span className="text-ink-muted text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((a) => (
        <span
          key={a}
          title={t(`allergens.${a}`)}
          className={`inline-flex items-center justify-center rounded border border-warn/30 bg-warn/10 text-warn font-medium ${
            compact ? "px-1 py-0 text-[10px] min-w-[22px] h-[18px]" : "px-1.5 py-0.5 text-[10px]"
          }`}
        >
          {compact ? ALLERGEN_ABBR[a] || a.slice(0, 2) : t(`allergens.${a}`)}
        </span>
      ))}
    </div>
  );
}

/* ----- ingredient picker used inside the recipe form ----- */
function IngredientPicker({ ingredients, onPick, exclude = [] }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return ingredients
      .filter((i) => !exclude.includes(i.id))
      .filter((i) => !ql || i.name.toLowerCase().includes(ql))
      .slice(0, 8);
  }, [q, ingredients, exclude]);

  return (
    <div data-testid="ingredient-picker">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("recipes.search_ingredient")}
          className="!pl-9"
          data-testid="picker-search"
        />
      </div>
      {q && (
        <div className="mt-2 rounded-md border border-white/10 bg-bg-void max-h-64 overflow-y-auto divide-y divide-white/5">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-ink-muted">Keine Treffer.</div>
          ) : (
            filtered.map((i) => (
              <button
                type="button"
                key={i.id}
                onClick={() => { onPick(i); setQ(""); }}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-white/[0.03] transition-colors"
                data-testid={`picker-pick-${i.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {i.is_seasonal && <Leaf size={12} className="text-ok shrink-0" />}
                  <span className="text-sm text-ink-primary truncate">{i.name}</span>
                  <Badge>{i.category}</Badge>
                </div>
                <span className="num text-xs text-brand">
                  {formatEUR(i.price_per_unit, 4)} / {UNIT_LABELS[i.unit] || i.unit}
                </span>
              </button>
            ))
          )}
        </div>
      )}
      {q && filtered.length === 0 && (
        <IngredientMatchSuggestion
          name={q}
          onAccept={(existing) => {
            onPick({
              id: existing.id, name: existing.name, category: existing.category,
              unit: existing.unit, price_per_unit: existing.price_per_unit,
            });
            setQ("");
          }}
        />
      )}
    </div>
  );
}

/* ----- recipe slide-over form ----- */
function RecipeSlideOver({ open, onClose, mode, initial, meta, ingredients, targetFoodCost, onSaved }) {
  const { t } = useTranslation();

  const emptyForm = {
    name: "", category: "Hauptgang", portions: "4", portion_size: "",
    selling_price: "",
    labor_cost_per_portion: "0",
    overhead_per_portion: "0",
    ingredients: [], // [{ingredient_id, quantity, unit}]
    menu_description: "",
    preparation_steps: "",
    photo: "",
    status: "active",
  };
  const [form, setForm] = useState(emptyForm);
  const [section, setSection] = useState("basics");
  const [saving, setSaving] = useState(false);

  // Phase 29 — Auto-Save (30s) into localStorage with restore banner.
  const draftScope = "recipe-builder";
  const draftId = mode === "edit" && initial?.id ? initial.id : "new";
  const { draftFound, loadDraft, clearDraft, dismissDraft, lastSavedAt } = useAutoSaveDraft({
    scope: draftScope, id: draftId, value: form, enabled: open,
  });

  const restoreDraft = () => {
    const v = loadDraft();
    if (v) {
      setForm(v);
      toast.success(t("drafts.restored", "Entwurf wiederhergestellt"));
    }
    dismissDraft();
  };
  const discardDraft = () => {
    clearDraft();
  };

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setForm({
        name: initial.name || "",
        category: initial.category || "Hauptgang",
        portions: String(initial.portions ?? "4").replace(".", ","),
        portion_size: initial.portion_size || "",
        selling_price: initial.selling_price != null ? String(initial.selling_price).replace(".", ",") : "",
        labor_cost_per_portion: String(initial.labor_cost_per_portion ?? "0").replace(".", ","),
        overhead_per_portion: String(initial.overhead_per_portion ?? "0").replace(".", ","),
        ingredients: (initial.ingredients || []).map((l) => ({
          ingredient_id: l.ingredient_id, quantity: String(l.quantity).replace(".", ","), unit: l.unit,
        })),
        menu_description: initial.menu_description || "",
        preparation_steps: initial.preparation_steps || "",
        photo: initial.photo || "",
        status: initial.status || "active",
      });
    } else {
      setForm(emptyForm);
    }
    setSection("basics");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initial]);

  const ingredientMap = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);

  /* ----- live cost preview (client-side) ----- */
  const live = useMemo(() => {
    const portions = parseATDecimal(form.portions) || 1;
    let total = 0;
    const allergenSet = new Set();
    let autoSeasonal = false;
    const lines = form.ingredients.map((l) => {
      const ing = ingredientMap[l.ingredient_id];
      const qty = parseATDecimal(l.quantity);
      const { cost, incompatible } = lineCost(qty, l.unit, ing);
      total += cost;
      if (ing) {
        (ing.allergens || []).forEach((a) => allergenSet.add(a));
        if (ing.is_seasonal) autoSeasonal = true;
      }
      return { ...l, cost, incompatible, ingredient: ing };
    });
    const cpp = total / portions;
    const sell = parseATDecimal(form.selling_price);
    const suggested = targetFoodCost > 0 ? cpp / (targetFoodCost / 100) : 0;
    const effective = sell > 0 ? sell : suggested;
    const fcp = effective > 0 ? (cpp / effective) * 100 : 0;
    const labor = parseATDecimal(form.labor_cost_per_portion);
    const oh = parseATDecimal(form.overhead_per_portion);
    const db1 = effective - cpp;
    const db2 = db1 - labor - oh;
    let trafficLight = "neutral";
    if (fcp > 0) {
      if (fcp <= targetFoodCost) trafficLight = "green";
      else if (fcp <= targetFoodCost + 5) trafficLight = "yellow";
      else trafficLight = "red";
    }
    return {
      lines, total, cpp, suggested, effective, fcp, db1, db2, trafficLight,
      allergens: Array.from(allergenSet).sort(), autoSeasonal,
    };
  }, [form, ingredientMap, targetFoodCost]);

  const addLine = (ing) => {
    setForm((s) => ({
      ...s,
      ingredients: [...s.ingredients, { ingredient_id: ing.id, quantity: "100", unit: ing.unit }],
    }));
  };

  const removeLine = (idx) => {
    setForm((s) => ({ ...s, ingredients: s.ingredients.filter((_, i) => i !== idx) }));
  };

  const updateLine = (idx, patch) => {
    setForm((s) => ({
      ...s,
      ingredients: s.ingredients.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));
  };

  const handlePhoto = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t("recipes.photo_too_large")); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        portions: parseATDecimal(form.portions) || 1,
        portion_size: form.portion_size || "",
        labor_cost_per_portion: parseATDecimal(form.labor_cost_per_portion),
        overhead_per_portion: parseATDecimal(form.overhead_per_portion),
        ingredients: form.ingredients
          .filter((l) => l.ingredient_id && parseATDecimal(l.quantity) > 0)
          .map((l) => ({ ingredient_id: l.ingredient_id, quantity: parseATDecimal(l.quantity), unit: l.unit })),
        menu_description: form.menu_description || "",
        preparation_steps: form.preparation_steps || "",
        photo: form.photo || "",
        status: form.status,
      };
      const sellNum = parseATDecimal(form.selling_price);
      if (sellNum > 0) payload.selling_price = sellNum;

      if (mode === "edit") {
        // null out selling_price if user blanked it
        if (!(sellNum > 0)) payload.selling_price = null;
        await api.patch(`/recipes/${initial.id}`, payload);
        toast.success("Rezept aktualisiert.");
      } else {
        await api.post("/recipes", payload);
        toast.success("Rezept gespeichert.");
      }
      clearDraft();   // Phase 29 — drop the localStorage draft once persisted
      onSaved && onSaved();
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const sections = ["basics", "ingredients", "pricing", "presentation"];
  const labels = t("recipes.sections", { returnObjects: true });

  return (
    <div className="fixed inset-0 z-50" data-testid="recipe-slideover">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm fade-in" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 w-full sm:w-[680px] bg-bg-surface border-l border-white/10 shadow-modal flex flex-col slide-in">
        <header className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1">
              {mode === "edit" ? "Bearbeiten" : "Neu"}
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight">
              {mode === "edit" ? t("recipes.form_edit") : t("recipes.form_add")}
            </h2>
          </div>
          <button onClick={onClose} className="text-ink-secondary hover:text-ink-primary" data-testid="recipe-slide-close">
            <X size={18} />
          </button>
        </header>

        {/* Phase 29 — auto-save draft banner + status */}
        {draftFound && (
          <DraftRestoreBanner draft={draftFound} onRestore={restoreDraft} onDiscard={discardDraft} />
        )}
        {lastSavedAt && (
          <div className="px-6 pt-2 text-[10px] text-ink-muted" data-testid="recipe-draft-status">
            {t("drafts.auto_saved_at", "Entwurf zwischengespeichert um {{time}}", {
              time: new Date(lastSavedAt).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
            })}
          </div>
        )}

        {/* Section tabs */}
        <div className="border-b border-white/10 px-6 py-2 flex items-center gap-1 bg-bg-void/40">
          {sections.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSection(s)}
              data-testid={`recipe-section-${s}`}
              className={`px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider rounded transition-colors ${
                section === s
                  ? "bg-brand/15 text-brand"
                  : "text-ink-secondary hover:text-ink-primary hover:bg-white/5"
              }`}
            >
              {labels[s]}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-6" data-testid="recipe-form">
          {/* ----- BASICS ----- */}
          {section === "basics" && (
            <div className="space-y-5">
              <div>
                <Label>{t("recipes.name")}</Label>
                <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} required data-testid="recipe-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("recipes.category")}</Label>
                  <Select value={form.category} onChange={(e) => setForm((s) => ({ ...s, category: e.target.value }))} data-testid="recipe-form-category">
                    {(meta?.categories || []).map((c) => (
                      <option key={c} value={c}>{RECIPE_CATEGORY_LABELS[c] || c}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label>{t("recipes.status")}</Label>
                  <Select value={form.status} onChange={(e) => setForm((s) => ({ ...s, status: e.target.value }))} data-testid="recipe-form-status">
                    <option value="active">{t("recipes.status_active")}</option>
                    <option value="inactive">{t("recipes.status_inactive")}</option>
                    <option value="seasonal">{t("recipes.status_seasonal")}</option>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("recipes.portions")}</Label>
                  <Input inputMode="decimal" value={form.portions} onChange={(e) => setForm((s) => ({ ...s, portions: e.target.value }))} required data-testid="recipe-form-portions" />
                </div>
                <div>
                  <Label>{t("recipes.portion_size")}</Label>
                  <Input value={form.portion_size} onChange={(e) => setForm((s) => ({ ...s, portion_size: e.target.value }))} placeholder={t("recipes.portion_size_placeholder")} data-testid="recipe-form-portion-size" />
                </div>
              </div>
            </div>
          )}

          {/* ----- INGREDIENTS ----- */}
          {section === "ingredients" && (
            <div className="space-y-4">
              {ingredients.length === 0 ? (
                <Card className="px-4 py-6 text-center">
                  <p className="text-sm text-ink-secondary">{t("recipes.no_ingredients_in_db")}</p>
                </Card>
              ) : (
                <IngredientPicker
                  ingredients={ingredients}
                  exclude={form.ingredients.map((l) => l.ingredient_id)}
                  onPick={addLine}
                />
              )}

              {form.ingredients.length === 0 ? (
                <div className="text-xs text-ink-muted text-center py-6" data-testid="recipe-no-ingredients">
                  {t("recipes.no_ingredients")}
                </div>
              ) : (
                <Card className="overflow-hidden">
                  <table className="w-full text-sm" data-testid="recipe-line-table">
                    <thead>
                      <tr className="text-left">
                        <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-ink-secondary border-b border-white/10">{t("recipes.name")}</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-ink-secondary border-b border-white/10 text-right">{t("recipes.qty")}</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-ink-secondary border-b border-white/10">{t("recipes.unit")}</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-ink-secondary border-b border-white/10 text-right bg-brand/[0.04]">{t("recipes.line_cost")}</th>
                        <th className="px-2 border-b border-white/10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {live.lines.map((line, idx) => {
                        const ing = line.ingredient;
                        const compat = ing ? UNIT_COMPATIBLE(ing.unit) : ["g", "kg", "ml", "L", "Stueck", "Portion"];
                        return (
                          <tr key={idx} className="border-b border-white/5" data-testid={`recipe-line-${idx}`}>
                            <td className="px-3 py-2 text-ink-primary">
                              <div className="flex items-center gap-1.5">
                                {ing ? ing.name : <span className="text-err">— gelöscht —</span>}
                                {line.incompatible && (
                                  <span title={t("recipes.issue_incompatible_unit")} className="text-warn"><AlertTriangle size={12} /></span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <Input
                                inputMode="decimal"
                                value={form.ingredients[idx].quantity}
                                onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                                className="!py-1 !px-2 !text-xs !text-right"
                                data-testid={`recipe-line-qty-${idx}`}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <Select
                                value={form.ingredients[idx].unit}
                                onChange={(e) => updateLine(idx, { unit: e.target.value })}
                                className="!py-1 !px-2 !text-xs"
                                data-testid={`recipe-line-unit-${idx}`}
                              >
                                {compat.map((u) => <option key={u} value={u}>{UNIT_LABELS[u] || u}</option>)}
                              </Select>
                            </td>
                            <td className="px-3 py-2 num text-right text-ink-primary bg-brand/[0.02]" data-testid={`recipe-line-cost-${idx}`}>
                              {line.incompatible ? "—" : formatEUR(line.cost, 4)}
                            </td>
                            <td className="px-2 py-2">
                              <button type="button" onClick={() => removeLine(idx)} className="p-1.5 text-ink-secondary hover:text-err transition-colors" data-testid={`recipe-line-remove-${idx}`} aria-label="Remove">
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3} className="px-3 py-2.5 text-[11px] uppercase tracking-wider text-ink-secondary font-medium">
                          {t("recipes.total_cost")}
                        </td>
                        <td className="px-3 py-2.5 num text-right text-brand font-medium" data-testid="recipe-form-total">
                          {formatEUR(live.total)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </Card>
              )}

              {/* Allergens & seasonal hint */}
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <Label className="!mb-1">{t("common.allergens")}</Label>
                  <AllergenChips items={live.allergens} compact={false} />
                </div>
                {live.autoSeasonal && (
                  <div className="text-[11px] text-warn bg-warn/10 border border-warn/30 rounded px-2.5 py-1.5 flex items-center gap-1.5">
                    <Leaf size={12} /> {t("recipes.auto_seasonal_hint")}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ----- PRICING ----- */}
          {section === "pricing" && (
            <div className="space-y-5">
              <Card className="p-5 bg-brand/[0.04] border-brand/30">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("recipes.cost_per_portion")}</div>
                    <div className="num text-base font-medium">{formatEUR(live.cpp, 4)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("recipes.suggested_price")}</div>
                    <div className="num text-base font-medium">{formatEUR(live.suggested)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("recipes.target_food_cost")}</div>
                    <div className="num text-base font-medium">{formatNumber(targetFoodCost, 1)} %</div>
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("recipes.selling_price")} (€)</Label>
                  <Input
                    inputMode="decimal"
                    placeholder={t("recipes.selling_price_auto")}
                    value={form.selling_price}
                    onChange={(e) => setForm((s) => ({ ...s, selling_price: e.target.value }))}
                    data-testid="recipe-form-selling-price"
                  />
                </div>
                <div className="flex items-end">
                  <div className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("recipes.food_cost_pct")}</div>
                    <div className="flex items-center gap-2">
                      <TrafficDot light={live.trafficLight} />
                      <span className="num text-sm font-medium" data-testid="recipe-form-fcp">{live.fcp ? `${formatNumber(live.fcp, 1)} %` : "—"}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("recipes.labor")} (€)</Label>
                  <Input inputMode="decimal" value={form.labor_cost_per_portion} onChange={(e) => setForm((s) => ({ ...s, labor_cost_per_portion: e.target.value }))} data-testid="recipe-form-labor" />
                </div>
                <div>
                  <Label>{t("recipes.overhead")} (€)</Label>
                  <Input inputMode="decimal" value={form.overhead_per_portion} onChange={(e) => setForm((s) => ({ ...s, overhead_per_portion: e.target.value }))} data-testid="recipe-form-overhead" />
                  {(!form.overhead_per_portion || parseATDecimal(form.overhead_per_portion) === 0) && (
                    <div className="text-[10px] text-brand mt-1 flex items-center gap-1" data-testid="recipe-form-overhead-auto-hint">
                      <span className="inline-block w-1 h-1 rounded-full bg-brand" />
                      {t("recipes.overhead_auto_hint", "Wenn leer: Aus Betriebskosten (automatisch)")}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Card className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("recipes.db1")}</div>
                  <div className="num text-lg font-medium text-ink-primary" data-testid="recipe-form-db1">{formatEUR(live.db1)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("recipes.db2")}</div>
                  <div className={`num text-lg font-medium ${live.db2 < 0 ? "text-err" : "text-ink-primary"}`} data-testid="recipe-form-db2">{formatEUR(live.db2)}</div>
                </Card>
              </div>
            </div>
          )}

          {/* ----- PRESENTATION ----- */}
          {section === "presentation" && (
            <div className="space-y-5">
              <div>
                <Label>{t("recipes.menu_description")}</Label>
                <textarea
                  rows={3}
                  value={form.menu_description}
                  onChange={(e) => setForm((s) => ({ ...s, menu_description: e.target.value }))}
                  placeholder={t("recipes.menu_description_placeholder")}
                  className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand transition-all resize-none"
                  data-testid="recipe-form-menu-description"
                />
              </div>
              <div>
                <Label>{t("recipes.preparation")}</Label>
                <textarea
                  rows={6}
                  value={form.preparation_steps}
                  onChange={(e) => setForm((s) => ({ ...s, preparation_steps: e.target.value }))}
                  className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand transition-all resize-none"
                  data-testid="recipe-form-preparation"
                />
              </div>
              <div>
                <Label>{t("recipes.photo")}</Label>
                {form.photo ? (
                  <div className="relative w-full h-48 rounded-md overflow-hidden border border-white/10 bg-bg-void">
                    <img src={form.photo} alt="" className="w-full h-full object-cover" data-testid="recipe-form-photo-preview" />
                    <button type="button" onClick={() => setForm((s) => ({ ...s, photo: "" }))} className="absolute top-2 right-2 px-2.5 py-1 rounded bg-bg-void/90 border border-white/10 text-xs text-ink-secondary hover:text-err" data-testid="recipe-form-photo-remove">
                      {t("recipes.photo_remove")}
                    </button>
                  </div>
                ) : (
                  <label className="w-full h-32 border border-dashed border-white/15 rounded-md flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-brand/40 hover:bg-brand/[0.03] transition-colors" data-testid="recipe-form-photo-pick">
                    <ImagePlus size={20} className="text-ink-secondary" />
                    <span className="text-xs text-ink-secondary">{t("recipes.photo_pick")}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhoto(e.target.files?.[0])}
                      className="hidden"
                      data-testid="recipe-form-photo-input"
                    />
                  </label>
                )}
              </div>
            </div>
          )}
        </form>

        <footer className="border-t border-white/10 px-4 py-3 flex items-center justify-between bg-bg-void/40">
          <div className="text-xs text-ink-muted">
            {section !== "basics" && (
              <button type="button" onClick={() => setSection(sections[sections.indexOf(section) - 1])} className="hover:text-ink-primary transition-colors">← {labels[sections[sections.indexOf(section) - 1]]}</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {section !== "presentation" ? (
              <Button type="button" onClick={() => setSection(sections[sections.indexOf(section) + 1])} data-testid="recipe-form-next">
                {labels[sections[sections.indexOf(section) + 1]]} →
              </Button>
            ) : null}
            <Button variant="secondary" type="button" onClick={onClose} data-testid="recipe-form-cancel">{t("common.cancel")}</Button>
            <Button onClick={submit} disabled={saving} data-testid="recipe-form-save">{saving ? t("common.loading") : t("common.save")}</Button>
          </div>
        </footer>
      </aside>
    </div>
  );
}

/* ----- recipe detail (read-only slide-over) ----- */
function RecipeDetail({ recipe, onClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hasPlatingModule = (user?.modules || []).includes("plating");
  const [platingOpen, setPlatingOpen] = useState(false);
  if (!recipe) return null;
  const totals = recipe.totals || {};
  return (
    <div className="fixed inset-0 z-50" data-testid="recipe-detail">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm fade-in" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 w-full sm:w-[560px] bg-bg-surface border-l border-white/10 shadow-modal flex flex-col slide-in">
        <header className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1">
              {RECIPE_CATEGORY_LABELS[recipe.category] || recipe.category}
            </div>
            <h2 className="font-display text-xl font-semibold tracking-tight truncate">{recipe.name}</h2>
          </div>
          <div className="flex items-center gap-2">
            {hasPlatingModule && (
              <button
                onClick={() => setPlatingOpen(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-brand bg-brand/10 hover:bg-brand/20 transition"
                data-testid="recipe-detail-plating-btn"
                title="Plating Intelligence"
              >
                <UtensilsCrossed size={13} />
                <span className="hidden sm:inline">Plating Intelligence</span>
              </button>
            )}
            <button onClick={onClose} className="text-ink-secondary hover:text-ink-primary" data-testid="recipe-detail-close"><X size={18} /></button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {recipe.photo && (
            <img src={recipe.photo} alt={recipe.name} className="w-full h-48 object-cover rounded-md border border-white/10" />
          )}

          <SeasonalWarning recipeId={recipe.id} category={recipe.category} />

          <Card className="p-5 bg-brand/[0.04] border-brand/30">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("recipes.cost_per_portion")}</div>
                <div className="num text-lg font-medium">{formatEUR(totals.cost_per_portion, 4)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("recipes.selling_price")}</div>
                <div className="num text-lg font-medium">{formatEUR(totals.effective_selling_price)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("recipes.food_cost_pct")}</div>
                <div className="flex items-center gap-2 num text-lg font-medium">
                  <TrafficDot light={totals.traffic_light} />
                  {formatNumber(totals.food_cost_pct, 1)} %
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-brand mb-1">{t("recipes.target_food_cost")}</div>
                <div className="num text-lg font-medium">{formatNumber(totals.target_food_cost, 1)} %</div>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("recipes.db1")}</div>
              <div className="num text-base font-medium">{formatEUR(totals.db1)}</div>
            </Card>
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1">{t("recipes.db2")}</div>
              <div className={`num text-base font-medium ${totals.db2 < 0 ? "text-err" : ""}`}>{formatEUR(totals.db2)}</div>
              {totals.overhead_source === "business_cost_profile" && totals.overhead_per_portion > 0 && (
                <div className="text-[10px] text-brand mt-1.5 flex items-center gap-1" data-testid="recipe-db2-auto-badge">
                  <span className="inline-block w-1 h-1 rounded-full bg-brand" />
                  {t("recipes.db2_auto_overhead", "Overhead {{val}} · Aus Betriebskosten (automatisch)", { val: formatEUR(totals.overhead_per_portion) })}
                </div>
              )}
            </Card>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("recipes.ingredients")}</div>
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {(recipe.ingredients || []).map((l, i) => (
                    <tr key={i} className="border-b border-white/5 last:border-0">
                      <td className="px-3 py-2 text-ink-primary">
                        {l.ingredient_name}
                        {l.incompatible_unit && <span className="ml-1.5 text-warn"><AlertTriangle size={11} className="inline" /></span>}
                      </td>
                      <td className="px-3 py-2 num text-ink-secondary text-right text-xs">{l.quantity} {UNIT_LABELS[l.unit] || l.unit}</td>
                      <td className="px-3 py-2 num text-right text-ink-primary">{formatEUR(l.line_cost, 4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("common.allergens")}</div>
            <AllergenChips items={recipe.allergens} compact={false} />
          </div>

          {recipe.menu_description && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("recipes.menu_description")}</div>
              <p className="text-sm text-ink-secondary leading-relaxed whitespace-pre-wrap">{recipe.menu_description}</p>
            </div>
          )}
          {recipe.preparation_steps && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("recipes.preparation")}</div>
              <p className="text-sm text-ink-secondary leading-relaxed whitespace-pre-wrap">{recipe.preparation_steps}</p>
            </div>
          )}

          {(recipe.issues || []).length > 0 && (
            <Card className="p-3 border-warn/30 bg-warn/[0.04]">
              <div className="text-[10px] uppercase tracking-wider text-warn mb-1 flex items-center gap-1">
                <AlertTriangle size={11} /> {t("recipes.issues_title")}
              </div>
              <ul className="text-xs text-ink-secondary space-y-1">
                {recipe.issues.map((iss, i) => (
                  <li key={i}>{iss.issue === "incompatible_unit" ? t("recipes.issue_incompatible_unit") : t("recipes.issue_missing")}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </aside>
      {platingOpen && (
        <PlatingModal recipe={recipe} onClose={() => setPlatingOpen(false)} />
      )}
    </div>
  );
}

/* ----- main page ----- */
function SeasonalWarning({ recipeId, category }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/insights/seasonal/recipe/${recipeId}`);
        if (!cancelled) setData(r.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [recipeId]);

  if (!data || !data.has_warning) return null;

  const goToAI = () => {
    const cat = category || "Hauptgang";
    navigate(`/ai-generator?category=${encodeURIComponent(cat)}&seasonal=true`);
  };

  return (
    <Card className="p-3 border-warn/40 bg-warn/5" data-testid="recipe-seasonal-warning">
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="text-warn mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-medium text-ink-primary mb-0.5">
            {t("recipes_extra.season_warning", { pct: data.estimated_premium_pct })}
          </div>
          <div className="text-xs text-ink-muted mb-2">
            {data.flagged_ingredients.map((f) => f.label || f.ingredient_name).join(" · ")}
          </div>
          <Button size="sm" variant="secondary" onClick={goToAI} data-testid="recipe-season-ai">
            <Sparkles size={11} /> {t("recipes_extra.season_ai_alt")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function RecipesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hasPlatingModule = (user?.modules || []).includes("plating");
  const [platingFor, setPlatingFor] = useState(null);
  const [items, setItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [meta, setMeta] = useState(null);
  const [targetFoodCost, setTargetFoodCost] = useState(30);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [slideOpen, setSlideOpen] = useState(false);
  const [showPdfImport, setShowPdfImport] = useState(false);
  const [slideMode, setSlideMode] = useState("add");
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const loadInit = async () => {
    try {
      const [m, ing] = await Promise.all([
        api.get("/recipes/meta"),
        api.get("/ingredients"),
      ]);
      setMeta(m.data);
      setTargetFoodCost(Number(m.data.target_food_cost) || 30);
      setIngredients(ing.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (category) params.category = category;
      if (status) params.status = status;
      const { data } = await api.get("/recipes", { params });
      setItems(data.items);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadInit(); }, []);
  useEffect(() => {
    const tm = setTimeout(load, 250);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, status]);

  const onDelete = async (id) => {
    if (!window.confirm(t("recipes.delete_confirm"))) return;
    try {
      await api.delete(`/recipes/${id}`);
      toast.success("Gelöscht.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openAdd = () => { setSlideMode("add"); setEditing(null); setSlideOpen(true); };
  const openEdit = async (r) => {
    try {
      const { data } = await api.get(`/recipes/${r.id}`);
      setSlideMode("edit"); setEditing(data); setSlideOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openDetail = async (r) => {
    try {
      const { data } = await api.get(`/recipes/${r.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Phase 30 — Excel export
  const exportXlsx = async () => {
    try {
      const res = await api.get("/recipes/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date();
      const ddmmyyyy = `${String(today.getDate()).padStart(2, "0")}${String(today.getMonth() + 1).padStart(2, "0")}${today.getFullYear()}`;
      a.download = `StudioOps_Rezepte_${ddmmyyyy}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="recipes-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1.5">Modul · 02</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight flex items-center gap-3">
            <ChefHat size={28} className="text-brand" /> {t("recipes.title")}
          </h1>
          <p className="text-sm text-ink-secondary mt-1">{t("recipes.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone="brand">
            <Sparkles size={11} /> {t("recipes.target_food_cost")} {formatNumber(targetFoodCost, 1)}%
          </Badge>
          <UnifiedImportButton
            acceptCsvXlsx={false}
            acceptPdf
            onPdfFile={() => setShowPdfImport(true)}
            testId="recipe-import-btn"
          />
          <Button variant="secondary" onClick={exportXlsx} data-testid="recipes-export-btn">
            <FileText size={14} /> {t("recipes.export", "Excel-Export")}
          </Button>
          <Button onClick={openAdd} data-testid="add-recipe-button">
            <Plus size={14} /> {t("recipes.add")}
          </Button>
        </div>
      </div>

      {showPdfImport && (
        <RecipePdfImportModal
          onClose={() => setShowPdfImport(false)}
          onDone={() => { setShowPdfImport(false); load(); }}
        />
      )}

      <Card className="px-3 py-2.5 mb-4 flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("recipes.search_placeholder")}
            className="!pl-9 !border-transparent !bg-transparent"
            data-testid="recipes-search"
          />
        </div>
        <div className="hidden md:block w-px h-6 bg-white/10" />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} className="md:max-w-[200px]" data-testid="recipes-category-filter">
          <option value="">{t("recipes.all_categories")}</option>
          {(meta?.categories || []).map((c) => <option key={c} value={c}>{RECIPE_CATEGORY_LABELS[c] || c}</option>)}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="md:max-w-[180px]" data-testid="recipes-status-filter">
          <option value="">{t("recipes.all_statuses")}</option>
          <option value="active">{t("recipes.status_active")}</option>
          <option value="inactive">{t("recipes.status_inactive")}</option>
          <option value="seasonal">{t("recipes.status_seasonal")}</option>
        </Select>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-20 flex items-center justify-center"><Spinner size={20} /></div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={ChefHat}
            title={t("recipes.empty_title")}
            description={t("recipes.empty_subtitle")}
            action={<Button onClick={openAdd} data-testid="empty-add-recipe"><Plus size={14} /> {t("recipes.add")}</Button>}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recipes-table">
              <thead>
                <tr className="text-left">
                  {[
                    t("recipes.name"), t("recipes.category"), t("recipes.portions"),
                    t("recipes.cost_per_portion"), t("recipes.selling_price"),
                    t("recipes.food_cost_pct"), t("recipes.db1"),
                    t("recipes.allergens"), t("recipes.status"), "",
                  ].map((h, i) => (
                    <th
                      key={i}
                      className={`text-[10px] uppercase tracking-[0.05em] font-medium text-ink-secondary px-4 py-3 border-b border-white/10 ${
                        [3, 4, 5, 6].includes(i) ? "text-right" : ""
                      } ${i === 5 ? "bg-brand/[0.04]" : ""}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((r, idx) => (
                  <tr
                    key={r.id}
                    onClick={() => openDetail(r)}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer group"
                    data-testid={`recipe-row-${idx}`}
                  >
                    <td className="px-4 py-2.5 text-ink-primary font-medium">{r.name}</td>
                    <td className="px-4 py-2.5"><Badge>{RECIPE_CATEGORY_LABELS[r.category] || r.category}</Badge></td>
                    <td className="px-4 py-2.5 num text-ink-secondary text-xs">{r.portions}</td>
                    <td className="px-4 py-2.5 num text-ink-primary text-right">{formatEUR(r.totals?.cost_per_portion)}</td>
                    <td className="px-4 py-2.5 num text-ink-primary text-right">{formatEUR(r.totals?.effective_selling_price)}</td>
                    <td className="px-4 py-2.5 num text-right bg-brand/[0.03]" data-testid={`recipe-row-fcp-${idx}`}>
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        <TrafficDot light={r.totals?.traffic_light} />
                        {r.totals?.food_cost_pct ? `${formatNumber(r.totals.food_cost_pct, 1)} %` : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 num text-right">{formatEUR(r.totals?.db1)}</td>
                    <td className="px-4 py-2.5"><AllergenChips items={r.allergens} compact /></td>
                    <td className="px-4 py-2.5">
                      <Badge tone={STATUS_TONES[r.status] || "default"}>
                        {r.status === "active" ? t("recipes.status_active") : r.status === "inactive" ? t("recipes.status_inactive") : t("recipes.status_seasonal")}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                        {hasPlatingModule && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setPlatingFor(r); }}
                            className="p-1.5 rounded text-ink-secondary hover:text-brand hover:bg-brand/10 transition-colors"
                            data-testid={`recipe-plating-${idx}`}
                            aria-label="Plating Intelligence"
                            title="Plating Intelligence"
                          >
                            <UtensilsCrossed size={13} />
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="p-1.5 rounded text-ink-secondary hover:text-brand hover:bg-brand/10 transition-colors" data-testid={`recipe-edit-${idx}`} aria-label="Edit">
                          <Pencil size={13} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); onDelete(r.id); }} className="p-1.5 rounded text-ink-secondary hover:text-err hover:bg-err/10 transition-colors" data-testid={`recipe-delete-${idx}`} aria-label="Delete">
                          <Trash2 size={13} />
                        </button>
                        <ChevronRight size={13} className="text-ink-muted ml-1" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <RecipeSlideOver
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        mode={slideMode}
        initial={editing}
        meta={meta}
        ingredients={ingredients}
        targetFoodCost={targetFoodCost}
        onSaved={load}
      />
      <RecipeDetail recipe={detail} onClose={() => setDetail(null)} />
      {platingFor && (
        <PlatingModal recipe={platingFor} onClose={() => setPlatingFor(null)} />
      )}
    </div>
  );
}
